import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Alert,
  Collapse,
  CircularProgress,
  Stack,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Schedule as ScheduleIcon,
  SmartToy as AgentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  DoNotDisturb as SkippedIcon,
  OpenInNew as OpenInNewIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  agentScheduleApi,
  agentApi,
  aiSystemApi,
  aiEventTypeApi,
  alertApi,
} from '../../services/api';
import { AgentFormDialog } from '../agent/AgentFormDialog';
import {
  AgentAutocomplete,
  ConversationAutocomplete,
  type AgentSelectorOption,
} from '../common/AsyncEntitySelect';
import { useAuth } from '../../context/AuthContext';
import {
  EventFilterFields,
  EventTypeAutocomplete,
  buildEventFilters,
  createEmptyEventFilterValue,
  eventTypeAccessErrorMessage,
  eventFilterValueFromFilters,
  getAccessibleEventTypes,
  type EventFilterFormValue,
} from '../schedule/EventTypeAutocomplete';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import type { 
  AgentSchedule, 
  AgentScheduleCreate, 
  AgentScheduleUpdate,
  AgentScheduleType,
  AgentScheduleRecurringUnit,
  AgentScheduleWeekDay,
  AgentScheduleExecution,
  AgentScheduleTimeRange,
} from '../../types/agent';
import type { AIEventTriggerConfig } from '../../types/schedule';
import { resolveDefaultScheduleAgentId } from './scheduleAgentDefaults';

type AgentScheduleScope = 'all' | 'system_agent' | 'direct_agent';

interface AgentSchedulePanelProps {
  systemId?: number;
  agentId?: number; // Optional: filter by specific agent
  agentName?: string;
  scheduleScope?: AgentScheduleScope;
  activeConversationId?: string | null;
  activeConversationAgentId?: number | null;
}

interface ScheduleFormData {
  name: string;
  description: string;
  agent: number | '';
  target_ai_system: number | '';
  conversation_session: string | null;
  create_conversation_if_missing: boolean;
  task_prompt: string;
  schedule_type: AgentScheduleType;
  start_at: string;
  end_at: string | null;
  recurring_unit: AgentScheduleRecurringUnit | null;
  recurring_value: number | null;
  excluded_days: AgentScheduleWeekDay[] | null;
  excluded_time_ranges: AgentScheduleTimeRange[] | null;
  event_config: AIEventTriggerConfig | null;
  max_iterations: number;
  timeout_seconds: number;
  is_active: boolean;
}

const WEEKDAYS: { value: AgentScheduleWeekDay; label: string }[] = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

const RECURRING_UNITS: { value: AgentScheduleRecurringUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
];

const getStatusIcon = (status: string | null) => {
  switch (status) {
    case 'SUCCESS':
      return <SuccessIcon fontSize="small" color="success" />;
    case 'ERROR':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'RUNNING':
    case 'PENDING':
      return <PendingIcon fontSize="small" color="warning" />;
    case 'SKIPPED':
      return <SkippedIcon fontSize="small" color="disabled" />;
    default:
      return null;
  }
};

const getStatusColor = (status: string | null): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'SUCCESS':
      return 'success';
    case 'ERROR':
      return 'error';
    case 'RUNNING':
    case 'PENDING':
      return 'warning';
    default:
      return 'default';
  }
};

export const AgentSchedulePanel: React.FC<AgentSchedulePanelProps> = ({
  systemId,
  agentId,
  agentName,
  scheduleScope = 'all',
  activeConversationId = null,
  activeConversationAgentId = null,
}) => {
  const queryClient = useQueryClient();
  const scheduleTriggerAccess = useActionAccess(AI_ACTION_SURFACES.scheduleTrigger);
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true || user?.is_superuser === true;
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<AgentSchedule | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<AgentSchedule | null>(null);
  const [expandedSchedule, setExpandedSchedule] = useState<number | null>(null);
  const [triggeringId, setTriggeringId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [loadingScheduleDetails, setLoadingScheduleDetails] = useState(false);
  const [agentDetailOpen, setAgentDetailOpen] = useState(false);
  const [selectedAgentForDetail, setSelectedAgentForDetail] = useState<AgentSelectorOption | null>(null);
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);
  const [eventFilters, setEventFilters] = useState<EventFilterFormValue>(
    createEmptyEventFilterValue()
  );
  const scheduleQueryKey = ['agent-schedules', systemId ?? null, agentId ?? null, scheduleScope];
  const isGlobalSystemAgentSchedule = scheduleScope === 'system_agent' && !systemId;
  const isGlobalDirectAgentSchedule = scheduleScope === 'direct_agent' && !systemId;
  const isDirectAgentSchedule =
    scheduleScope === 'direct_agent' ||
    Boolean(agentId && !systemId && scheduleScope !== 'system_agent');
  const showSystemColumn = !systemId && !isGlobalDirectAgentSchedule;

  const [formData, setFormData] = useState<ScheduleFormData>({
    name: '',
    description: '',
    agent: '',
    target_ai_system: '',
    conversation_session: null,
    create_conversation_if_missing: false,
    task_prompt: '',
    schedule_type: 'RECURRING',
    start_at: '',
    end_at: null,
    recurring_unit: 'hours',
    recurring_value: 1,
    excluded_days: ['SATURDAY', 'SUNDAY'],
    excluded_time_ranges: null,
    event_config: null,
    max_iterations: 15,
    timeout_seconds: 300,
    is_active: true,
  });

  // Fetch one page only for availability checks; the dropdown loads searchable pages itself.
  const { data: agentsData, isLoading: isAgentsLoading } = useQuery({
    queryKey: ['agents-for-schedule', agentId ?? null],
    queryFn: () => agentApi.list(1, 1, { is_active: true }),
    enabled: !agentId,
  });

  const { data: systemsData, isLoading: isSystemsLoading } = useQuery({
    queryKey: ['ai-systems-for-agent-schedule'],
    queryFn: () => aiSystemApi.list({ page_size: 500 }),
    enabled: isGlobalSystemAgentSchedule,
  });

  const {
    data: eventTypes = [],
    error: eventTypesError,
    isFetching: isEventTypesLoading,
  } = useQuery({
    queryKey: ['ai-event-types', { includeAdmin: isAdmin }],
    queryFn: () => aiEventTypeApi.list({ includeAdmin: isAdmin }),
    enabled: formOpen && formData.schedule_type === 'EVENT' && !!user,
  });
  const eventTypesErrorMessage = eventTypeAccessErrorMessage(eventTypesError);
  const accessibleEventTypes = useMemo(
    () => getAccessibleEventTypes(eventTypes, isAdmin),
    [eventTypes, isAdmin]
  );
  const selectedEventType = formData.event_config?.event_type || '';
  const selectedEventTypeDefinition =
    accessibleEventTypes.find((eventType) => eventType.value === selectedEventType) || null;
  const selectedEventTypeIsAccessible =
    formData.schedule_type !== 'EVENT' || selectedEventTypeDefinition !== null;
  const selectedEventRequiresAlert =
    selectedEventTypeDefinition?.value === 'alerts.triggered' &&
    !eventFilters.payloadFields.alert_id;
  const { data: alerts = [], isFetching: isAlertsLoading } = useQuery({
    queryKey: ['alerts', 'agent-event-schedule-picker'],
    queryFn: () => alertApi.list(),
    enabled: formOpen && selectedEventTypeDefinition?.value === 'alerts.triggered',
  });

  const fixedAgentOption = agentId
    ? {
        id: agentId,
        name: agentName || `Agent ${agentId}`,
        description: '',
      }
    : null;
  const systems = systemsData?.results || [];
  const isScheduleResourceLoading =
    (!agentId && isAgentsLoading) || (isGlobalSystemAgentSchedule && isSystemsLoading);
  const canCreateSchedule = Boolean(
    !isScheduleResourceLoading &&
    (agentId || (agentsData?.count ?? 0) > 0) &&
    (!isGlobalSystemAgentSchedule || systems.length > 0)
  );
  const panelTitle = isGlobalSystemAgentSchedule
    ? 'AI System Agent Schedules'
    : isDirectAgentSchedule
      ? 'Agent Schedules'
      : 'AI Manager Schedules';
  const panelDescription = isGlobalSystemAgentSchedule
    ? 'Schedule agents to run one-time, recurring, or event-triggered tasks against selected AI systems.'
    : isDirectAgentSchedule
      ? 'Schedule agents to run one-time, recurring, or event-triggered tasks automatically.'
      : "Schedule the AI Manager to automatically analyze this system's performance and provide optimization recommendations on a regular basis.";

  // Fetch agent schedules
  const { data: schedules, isLoading } = useQuery({
    queryKey: scheduleQueryKey,
    queryFn: () =>
      agentScheduleApi.list({
        ...(systemId ? { ai_system_id: systemId } : {}),
        ...(agentId ? { agent_id: agentId } : {}),
        ...(isGlobalSystemAgentSchedule ? { has_ai_system: true } : {}),
        ...(isDirectAgentSchedule ? { has_ai_system: false } : {}),
      }),
  });

  // Create schedule mutation
  const createMutation = useMutation({
    mutationFn: (data: AgentScheduleCreate) => agentScheduleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      handleCloseForm();
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.detail || JSON.stringify(error.response?.data || 'Failed to create schedule'));
    },
  });

  // Update schedule mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgentScheduleUpdate }) =>
      agentScheduleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      handleCloseForm();
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.detail || JSON.stringify(error.response?.data || 'Failed to update schedule'));
    },
  });

  // Delete schedule mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      setDeleteConfirmOpen(false);
      setScheduleToDelete(null);
    },
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.toggle(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: scheduleQueryKey });
      const previous = queryClient.getQueryData(scheduleQueryKey);
      queryClient.setQueryData(scheduleQueryKey, (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((s: AgentSchedule) =>
            s.id === id ? { ...s, is_active: !s.is_active } : s
          ),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(scheduleQueryKey, context.previous);
      }
      setTogglingId(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      setTogglingId(null);
    },
  });

  // Trigger schedule mutation
  const triggerMutation = useMutation({
    mutationFn: (id: number) => agentScheduleApi.trigger(id, false),
    onSuccess: (data) => {
      setTriggeringId(null);
      queryClient.invalidateQueries({ queryKey: scheduleQueryKey });
      // Could show a snackbar with data.message
    },
    onError: () => {
      setTriggeringId(null);
    },
  });

  // Fetch executions for expanded schedule
  const { data: executions } = useQuery({
    queryKey: ['agent-schedule-executions', expandedSchedule],
    queryFn: () => agentScheduleApi.getExecutions(expandedSchedule!, undefined, 10),
    enabled: !!expandedSchedule,
  });

  const formatDatetimeLocal = (isoString: string | null): string => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleOpenForm = async (schedule?: AgentSchedule) => {
    setFormError(null);
    if (schedule) {
      // Fetch full schedule details to get all fields (list endpoint doesn't include all)
      setLoadingScheduleDetails(true);
      setFormOpen(true);
      try {
        const fullSchedule = await agentScheduleApi.get(schedule.id);
        const triggerFilters = fullSchedule.event_trigger?.filters || {};
        setEditingSchedule(fullSchedule);
        setFormData({
          name: fullSchedule.name,
          description: fullSchedule.description || '',
          agent: fullSchedule.agent,
          target_ai_system: fullSchedule.target_ai_system || '',
          conversation_session: fullSchedule.conversation_session || null,
          create_conversation_if_missing:
            fullSchedule.create_conversation_if_missing || false,
          task_prompt: fullSchedule.task_prompt || '',
          schedule_type: fullSchedule.schedule_type,
          start_at: formatDatetimeLocal(fullSchedule.start_at),
          end_at: fullSchedule.end_at ? formatDatetimeLocal(fullSchedule.end_at) : null,
          recurring_unit: fullSchedule.recurring_unit,
          recurring_value: fullSchedule.recurring_value,
          excluded_days: fullSchedule.excluded_days,
          excluded_time_ranges: fullSchedule.excluded_time_ranges,
          event_config: fullSchedule.event_trigger
            ? {
                event_type: fullSchedule.event_trigger.event_type,
                filters: fullSchedule.event_trigger.filters || {},
                conditions: fullSchedule.event_trigger.conditions || [],
                cooldown_seconds: fullSchedule.event_trigger.cooldown_seconds || 0,
                dedupe_window_seconds: fullSchedule.event_trigger.dedupe_window_seconds || 60,
                max_runs_per_window: fullSchedule.event_trigger.max_runs_per_window ?? null,
                window_seconds: fullSchedule.event_trigger.window_seconds || 86400,
                while_running_policy: fullSchedule.event_trigger.while_running_policy || 'drop',
                input_mapping: fullSchedule.event_trigger.input_mapping || {},
                is_active: fullSchedule.event_trigger.is_active,
              }
            : null,
          max_iterations: fullSchedule.max_iterations || 15,
          timeout_seconds: fullSchedule.timeout_seconds || 300,
          is_active: fullSchedule.is_active,
        });
        setEventFilters(eventFilterValueFromFilters(triggerFilters));
      } catch (error) {
        console.error('Failed to load schedule details:', error);
        // Fallback to partial data from list
        setEditingSchedule(schedule);
        const triggerFilters = schedule.event_trigger?.filters || {};
        setFormData({
          name: schedule.name,
          description: schedule.description || '',
          agent: schedule.agent,
          target_ai_system: schedule.target_ai_system || '',
          conversation_session: schedule.conversation_session || null,
          create_conversation_if_missing:
            schedule.create_conversation_if_missing || false,
          task_prompt: schedule.task_prompt || '',
          schedule_type: schedule.schedule_type,
          start_at: formatDatetimeLocal(schedule.start_at),
          end_at: schedule.end_at ? formatDatetimeLocal(schedule.end_at) : null,
          recurring_unit: schedule.recurring_unit,
          recurring_value: schedule.recurring_value,
          excluded_days: schedule.excluded_days || null,
          excluded_time_ranges: schedule.excluded_time_ranges || null,
          event_config: schedule.event_trigger
            ? {
                event_type: schedule.event_trigger.event_type,
                filters: schedule.event_trigger.filters || {},
                conditions: schedule.event_trigger.conditions || [],
                cooldown_seconds: schedule.event_trigger.cooldown_seconds || 0,
                dedupe_window_seconds: schedule.event_trigger.dedupe_window_seconds || 60,
                max_runs_per_window: schedule.event_trigger.max_runs_per_window ?? null,
                window_seconds: schedule.event_trigger.window_seconds || 86400,
                while_running_policy: schedule.event_trigger.while_running_policy || 'drop',
                input_mapping: schedule.event_trigger.input_mapping || {},
                is_active: schedule.event_trigger.is_active,
              }
            : null,
          max_iterations: schedule.max_iterations || 15,
          timeout_seconds: schedule.timeout_seconds || 300,
          is_active: schedule.is_active,
        });
        setEventFilters(eventFilterValueFromFilters(triggerFilters));
      } finally {
        setLoadingScheduleDetails(false);
      }
    } else {
      setEditingSchedule(null);
      // Prefer the scoped agent, then the agent that owns the active
      // conversation, before falling back to the first available agent.
      const firstAvailableAgent = agentsData?.results?.[0] || null;
      const defaultAgent = resolveDefaultScheduleAgentId(
        agentId,
        activeConversationAgentId,
        firstAvailableAgent?.id
      );
      const defaultSystem =
        systemId || (isGlobalSystemAgentSchedule && systems.length > 0 ? systems[0].id : '');
      setSelectedAgentForDetail(
        fixedAgentOption ||
          (firstAvailableAgent?.id === defaultAgent ? firstAvailableAgent : null)
      );
      setFormData({
        name: '',
        description: '',
        agent: defaultAgent,
        target_ai_system: defaultSystem,
        conversation_session: null,
        create_conversation_if_missing: false,
        task_prompt: isDirectAgentSchedule
          ? 'Run this scheduled task and summarize the result.'
          : 'Analyze the trading performance of this AI System and provide optimization recommendations.',
        schedule_type: 'RECURRING',
        start_at: '',
        end_at: null,
        recurring_unit: 'hours',
        recurring_value: 24,
        excluded_days: ['SATURDAY', 'SUNDAY'],
        excluded_time_ranges: null,
        event_config: null,
        max_iterations: 15,
        timeout_seconds: 300,
        is_active: true,
      });
      setEventFilters(createEmptyEventFilterValue());
      setFormOpen(true);
    }
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingSchedule(null);
    setFormError(null);
  };

  const handleSubmit = () => {
    setFormError(null);
    const convertToISO = (datetimeLocal: string | null): string | null => {
      if (!datetimeLocal) return null;
      const localDate = new Date(datetimeLocal);
      return localDate.toISOString();
    };

    if (formData.agent === '') return;
    if (isGlobalSystemAgentSchedule && formData.target_ai_system === '') return;

    if (formData.schedule_type === 'EVENT' && eventTypesErrorMessage) {
      setFormError(eventTypesErrorMessage);
      return;
    }
    if (
      formData.schedule_type === 'EVENT' &&
      (!selectedEventTypeIsAccessible || selectedEventRequiresAlert)
    ) {
      setFormError(selectedEventRequiresAlert ? 'Please select an alert' : 'Please select an event type');
      return;
    }

    const eventConfig: AIEventTriggerConfig | null =
      formData.schedule_type === 'EVENT'
        ? {
            ...(formData.event_config || {}),
            event_type: selectedEventType,
            filters: buildEventFilters(selectedEventTypeDefinition, eventFilters),
            conditions: formData.event_config?.conditions || [],
            cooldown_seconds: formData.event_config?.cooldown_seconds || 0,
            dedupe_window_seconds: formData.event_config?.dedupe_window_seconds || 60,
            max_runs_per_window: formData.event_config?.max_runs_per_window ?? null,
            window_seconds: formData.event_config?.window_seconds || 86400,
            while_running_policy: formData.event_config?.while_running_policy || 'drop',
            input_mapping: formData.event_config?.input_mapping || {},
            is_active: true,
          }
        : null;

    const scheduleData: AgentScheduleCreate = {
      name: formData.name,
      description: formData.description,
      agent: formData.agent as number,
      task_prompt: formData.task_prompt,
      target_ai_system: isDirectAgentSchedule
        ? null
        : systemId ?? (formData.target_ai_system === '' ? null : formData.target_ai_system),
      conversation_session: formData.conversation_session,
      create_conversation_if_missing:
        formData.create_conversation_if_missing,
      schedule_type: formData.schedule_type,
      start_at:
        formData.schedule_type === 'EVENT'
          ? convertToISO(formData.start_at)
          : convertToISO(formData.start_at) || new Date().toISOString(),
      end_at: convertToISO(formData.end_at),
      recurring_unit: formData.schedule_type === 'RECURRING' ? formData.recurring_unit : null,
      recurring_value: formData.schedule_type === 'RECURRING' ? formData.recurring_value : null,
      excluded_days: formData.schedule_type === 'EVENT' ? null : formData.excluded_days,
      excluded_time_ranges: formData.excluded_time_ranges,
      event_config: eventConfig,
      max_iterations: formData.max_iterations,
      timeout_seconds: formData.timeout_seconds,
      is_active: formData.is_active,
    };

    if (editingSchedule) {
      updateMutation.mutate({ id: editingSchedule.id, data: scheduleData });
    } else {
      createMutation.mutate(scheduleData);
    }
  };

  const handleDelete = (schedule: AgentSchedule) => {
    setScheduleToDelete(schedule);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (scheduleToDelete) {
      deleteMutation.mutate(scheduleToDelete.id);
    }
  };

  const handleTrigger = (id: number) => {
    if (!scheduleTriggerAccess.ensureAccess()) return;
    setTriggeringId(id);
    triggerMutation.mutate(id);
  };

  const formatRecurring = (schedule: AgentSchedule) => {
    if (schedule.schedule_type === 'EVENT') {
      return schedule.event_trigger?.event_type_label || schedule.event_trigger?.event_type || 'Event trigger';
    }
    if (schedule.schedule_type === 'ONE_TIME') {
      return 'One-time';
    }
    if (schedule.recurring_value && schedule.recurring_unit) {
      return `Every ${schedule.recurring_value} ${schedule.recurring_unit}`;
    }
    return 'Recurring';
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <AgentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            {panelTitle}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            disabled={!canCreateSchedule}
          >
            Add Schedule
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {panelDescription}
        </Alert>

        {!isScheduleResourceLoading && !canCreateSchedule && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {isGlobalSystemAgentSchedule && systems.length === 0
              ? 'No AI systems are available to target.'
              : 'No active agents are available to schedule.'}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : schedules?.results && schedules.results.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Agent</TableCell>
                  {showSystemColumn && <TableCell>AI System</TableCell>}
                  <TableCell>Conversation</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Last Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.results.map((schedule) => (
                  <React.Fragment key={schedule.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedSchedule(
                            expandedSchedule === schedule.id ? null : schedule.id
                          )}
                        >
                          {expandedSchedule === schedule.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {schedule.name}
                        </Typography>
                        {schedule.description && (
                          <Typography variant="caption" color="text.secondary">
                            {schedule.description.substring(0, 50)}
                            {schedule.description.length > 50 ? '...' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.agent_name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      {showSystemColumn && (
                        <TableCell>
                          {schedule.target_ai_system_name ? (
                            <Chip
                              label={schedule.target_ai_system_name}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Direct
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {schedule.conversation_session ? (
                          <Typography
                            variant="body2"
                            title={schedule.conversation_session_title || undefined}
                            sx={{ maxWidth: 180 }}
                            noWrap
                          >
                            {schedule.conversation_session_title ||
                              `Conversation ${schedule.conversation_session.slice(0, 8)}`}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {schedule.create_conversation_if_missing
                              ? 'Create once, then reuse'
                              : 'Fresh each run'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatRecurring(schedule)}
                          size="small"
                          color={schedule.schedule_type === 'RECURRING' ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <span>{formatRelativeTime(schedule.last_execution_at)}</span>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(schedule.last_execution_status)}
                          <Chip
                            label={schedule.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={schedule.is_active ? 'success' : 'default'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.execution_count}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleTrigger(schedule.id)}
                            disabled={!schedule.is_active || triggeringId === schedule.id || scheduleTriggerAccess.disabled}
                            title={scheduleTriggerAccess.disabled ? scheduleTriggerAccess.blockedMessage : 'Run now'}
                          >
                            {triggeringId === schedule.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <PlayIcon />
                            )}
                          </IconButton>
                        </span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTogglingId(schedule.id);
                            toggleMutation.mutate(schedule.id);
                          }}
                          color={schedule.is_active ? 'warning' : 'success'}
                          disabled={togglingId === schedule.id}
                        >
                          {togglingId === schedule.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            schedule.is_active ? <PauseIcon /> : <PlayIcon />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenForm(schedule)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(schedule)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {/* Expanded row for executions */}
                    <TableRow>
                      <TableCell colSpan={showSystemColumn ? 10 : 9} sx={{ py: 0 }}>
                        <Collapse in={expandedSchedule === schedule.id}>
                          <Box sx={{ p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              <HistoryIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                              Task Prompt
                            </Typography>
                            <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'background.paper' }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {schedule.task_prompt}
                              </Typography>
                            </Paper>

                            <Typography variant="subtitle2" gutterBottom>
                              Recent Executions
                            </Typography>
                            {executions && executions.length > 0 ? (
                              <Stack spacing={1}>
                                {executions.map((exec: AgentScheduleExecution) => (
                                  <Paper key={exec.id} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusIcon(exec.status)}
                                        <Chip
                                          label={exec.status}
                                          size="small"
                                          color={getStatusColor(exec.status)}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                          {formatDateTime(exec.started_at)}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Tools: {exec.tool_calls_count}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Tokens: {exec.tokens_used}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {exec.error && (
                                      <Alert severity="error" sx={{ mt: 1 }}>
                                        {exec.error}
                                      </Alert>
                                    )}
                                    {exec.response && (
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          mt: 1, 
                                          maxHeight: 100, 
                                          overflow: 'auto',
                                          whiteSpace: 'pre-wrap',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        {exec.response.substring(0, 500)}
                                        {exec.response.length > 500 ? '...' : ''}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No executions yet
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No {panelTitle.toLowerCase()} configured
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {isGlobalSystemAgentSchedule
                ? 'Create a schedule to run an agent against a selected AI system automatically.'
                : isDirectAgentSchedule
                ? 'Create a schedule to run this agent automatically.'
                : 'Create a schedule to have the AI Manager automatically analyze this system and provide insights.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              disabled={!canCreateSchedule}
            >
              Create First Schedule
            </Button>
          </Paper>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSchedule ? 'Edit Agent Schedule' : 'Create Agent Schedule'}
        </DialogTitle>
        <DialogContent>
          {loadingScheduleDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading schedule details...</Typography>
            </Box>
          ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {formError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Schedule Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Daily Performance Analysis"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of what this schedule does"
              />
            </Grid>

            {/* Agent selector with shortcuts */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AgentAutocomplete
                  value={formData.agent || null}
                  onChange={(nextAgentId) =>
                    setFormData({
                      ...formData,
                      agent: nextAgentId || '',
                      conversation_session: null,
                    })
                  }
                  onOptionChange={setSelectedAgentForDetail}
                  label="Agent"
                  required
                  disabled={Boolean(agentId)}
                  filters={{ is_active: true }}
                  initialOption={fixedAgentOption}
                  startAdornment={<AgentIcon sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
                />
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (selectedAgentForDetail?.id === formData.agent) {
                        setAgentDetailOpen(true);
                      } else if (formData.agent) {
                        window.open(`/ai/agents/${formData.agent}`, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!formData.agent || Boolean(agentId)}
                    sx={{ mt: 1 }}
                  >
                    <ViewIcon />
                  </IconButton>
                </span>
                <IconButton
                  size="small"
                  onClick={() => setCreateAgentDialogOpen(true)}
                  disabled={Boolean(agentId)}
                  sx={{ mt: 1 }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {isDirectAgentSchedule
                  ? 'This schedule will run the selected agent directly'
                  : 'Select an agent to run scheduled analysis tasks'}
              </Typography>
            </Grid>

            {isGlobalSystemAgentSchedule && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <InputLabel>AI System</InputLabel>
                  <Select
                    value={formData.target_ai_system}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        target_ai_system: e.target.value as number,
                        conversation_session: null,
                      })
                    }
                    label="AI System"
                    disabled={systems.length === 0}
                  >
                    {systems.length === 0 ? (
                      <MenuItem value="" disabled>
                        No AI systems available
                      </MenuItem>
                    ) : (
                      systems.map((system) => (
                        <MenuItem key={system.id} value={system.id}>
                          {system.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  The schedule will run the selected agent with this AI system as execution context.
                </Typography>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <ConversationAutocomplete
                value={formData.conversation_session}
                onChange={(conversationSessionId) =>
                  setFormData({
                    ...formData,
                    conversation_session: conversationSessionId,
                  })
                }
                agentId={
                  typeof formData.agent === 'number' ? formData.agent : null
                }
                activeConversationId={activeConversationId}
                disabled={!formData.agent}
                helperText={
                  formData.conversation_session
                    ? 'Scheduled prompts and responses will continue in this conversation with its existing memory and history.'
                    : formData.create_conversation_if_missing
                      ? 'No conversation is linked yet. The first run will create one and later runs will reuse it.'
                      : 'Optional. Leave empty to create a fresh conversation for every scheduled run.'
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.create_conversation_if_missing}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        create_conversation_if_missing: e.target.checked,
                      })
                    }
                  />
                }
                label="Create and reuse a conversation when none is selected"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 6 }}
              >
                Disabled by default. When enabled, the first scheduled run creates
                one conversation and all later runs continue in it.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Task Prompt"
                fullWidth
                required
                multiline
                rows={4}
                value={formData.task_prompt}
                onChange={(e) => setFormData({ ...formData, task_prompt: e.target.value })}
                helperText="The task/question to ask the agent when the schedule triggers"
                placeholder={
                  isDirectAgentSchedule
                    ? 'Review recent activity and summarize action items.'
                    : 'Analyze the trading performance of this AI System for the past week and provide specific optimization recommendations.'
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>Schedule Configuration</Divider>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Schedule Type</InputLabel>
                <Select
                  value={formData.schedule_type}
                  onChange={(e) => {
                    const nextType = e.target.value as AgentScheduleType;
                    if (nextType !== 'EVENT') {
                      setEventFilters(createEmptyEventFilterValue());
                    }
                    setFormData({
                      ...formData,
                      schedule_type: nextType,
                      event_config: nextType === 'EVENT' ? formData.event_config : null,
                    });
                  }}
                  label="Schedule Type"
                >
                  <MenuItem value="ONE_TIME">One Time</MenuItem>
                  <MenuItem value="RECURRING">Recurring</MenuItem>
                  <MenuItem value="EVENT">Event Trigger</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label={formData.schedule_type === 'EVENT' ? 'Start At (Optional)' : 'Start At'}
                type="datetime-local"
                fullWidth
                required={formData.schedule_type !== 'EVENT'}
                value={formData.start_at}
                onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText={
                  formData.schedule_type === 'EVENT'
                    ? 'Optional event window start'
                    : 'When this schedule becomes eligible to run'
                }
              />
            </Grid>

            {formData.schedule_type === 'RECURRING' && (
              <>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    label="Every"
                    type="number"
                    fullWidth
                    value={formData.recurring_value || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      recurring_value: parseInt(e.target.value) || null 
                    })}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid size={{ xs: 6, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={formData.recurring_unit || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        recurring_unit: e.target.value as AgentScheduleRecurringUnit 
                      })}
                      label="Unit"
                    >
                      {RECURRING_UNITS.map((unit) => (
                        <MenuItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="End At (Optional)"
                    type="datetime-local"
                    fullWidth
                    value={formData.end_at || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      end_at: e.target.value || null 
                    })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty for no end date"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <InputLabel>Excluded Days</InputLabel>
                    <Select
                      multiple
                      value={formData.excluded_days || []}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        excluded_days: e.target.value as AgentScheduleWeekDay[] 
                      })}
                      label="Excluded Days"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {WEEKDAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {formData.schedule_type === 'EVENT' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <EventTypeAutocomplete
                    eventTypes={eventTypes}
                    value={selectedEventType}
                    onChange={(eventType) => {
                      setEventFilters(createEmptyEventFilterValue());
                      setFormData({
                        ...formData,
                        event_config: {
                          ...(formData.event_config || {}),
                          event_type: eventType,
                        },
                      });
                    }}
                    required
                    disabled={createMutation.isPending || updateMutation.isPending}
                    loading={isEventTypesLoading}
                    accessError={eventTypesErrorMessage}
                    isAdmin={isAdmin}
                  />
                  {selectedEventType && !selectedEventTypeIsAccessible && !isEventTypesLoading && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Selected event type is unavailable to this account. Choose an accessible event type.
                    </Alert>
                  )}
                </Grid>
                <EventFilterFields
                  eventType={selectedEventTypeDefinition}
                  value={eventFilters}
                  onChange={setEventFilters}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  alerts={alerts}
                  alertsLoading={isAlertsLoading}
                />
                {selectedEventRequiresAlert && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="warning">
                      Select the alert that should trigger this agent schedule.
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cooldown Seconds"
                    value={formData.event_config?.cooldown_seconds || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event_config: {
                          ...(formData.event_config || { event_type: selectedEventType }),
                          cooldown_seconds: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Dedupe Window Seconds"
                    value={formData.event_config?.dedupe_window_seconds || 60}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event_config: {
                          ...(formData.event_config || { event_type: selectedEventType }),
                          dedupe_window_seconds: parseInt(e.target.value) || 60,
                        },
                      })
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    select
                    label="While Running"
                    value={formData.event_config?.while_running_policy || 'drop'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        event_config: {
                          ...(formData.event_config || { event_type: selectedEventType }),
                          while_running_policy: e.target.value as 'drop' | 'queue',
                        },
                      })
                    }
                  >
                    <MenuItem value="drop">Drop</MenuItem>
                    <MenuItem value="queue">Queue</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="End At (Optional)"
                    type="datetime-local"
                    fullWidth
                    value={formData.end_at || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      end_at: e.target.value || null,
                    })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty for no event window end"
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>Execution Settings</Divider>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                label="Max Iterations"
                type="number"
                fullWidth
                value={formData.max_iterations}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  max_iterations: parseInt(e.target.value) || 10 
                })}
                inputProps={{ min: 1, max: 50 }}
                helperText="Max tool calls per execution"
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <TextField
                label="Timeout (seconds)"
                type="number"
                fullWidth
                value={formData.timeout_seconds}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  timeout_seconds: parseInt(e.target.value) || 300 
                })}
                inputProps={{ min: 60, max: 1800 }}
                helperText="Execution timeout"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Schedule Active"
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              loadingScheduleDetails ||
              createMutation.isPending || 
              updateMutation.isPending ||
              !formData.name ||
              !formData.agent ||
              (isGlobalSystemAgentSchedule && !formData.target_ai_system) ||
              !formData.task_prompt ||
              (formData.schedule_type === 'EVENT'
                ? !selectedEventType || selectedEventRequiresAlert
                : !formData.start_at)
            }
          >
            {editingSchedule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Schedule</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the schedule "{scheduleToDelete?.name}"?
            This will also delete all execution history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent Detail Dialog */}
      <Dialog 
        open={agentDetailOpen} 
        onClose={() => setAgentDetailOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgentIcon color="primary" />
          Agent Details: {selectedAgentForDetail?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedAgentForDetail && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{selectedAgentForDetail.name}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip 
                  label={selectedAgentForDetail.agent_type} 
                  size="small" 
                  color={selectedAgentForDetail.agent_type === 'AI_SYSTEM_BUILDER' ? 'primary' : 'default'}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body2">
                  {selectedAgentForDetail.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Model</Typography>
                <Typography variant="body2">{selectedAgentForDetail.model_name || 'Not specified'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip 
                  label={selectedAgentForDetail.is_active ? 'Active' : 'Inactive'} 
                  size="small" 
                  color={selectedAgentForDetail.is_active ? 'success' : 'default'}
                />
              </Grid>
              {selectedAgentForDetail.capabilities && selectedAgentForDetail.capabilities.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Capabilities</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedAgentForDetail.capabilities.map((cap: string, idx: number) => (
                      <Chip key={idx} label={cap} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
              )}
              {selectedAgentForDetail.tool_count !== undefined && selectedAgentForDetail.tool_count > 0 && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Assigned Tools</Typography>
                  <Typography variant="body2">{selectedAgentForDetail.tool_count} tools</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" color="text.secondary">Created</Typography>
                <Typography variant="body2">
                  {selectedAgentForDetail.created_at 
                    ? new Date(selectedAgentForDetail.created_at).toLocaleString() 
                    : 'Unknown'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentDetailOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              if (selectedAgentForDetail) {
                window.open(`/ai/agents/${selectedAgentForDetail.id}`, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            Open Full View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Agent Dialog */}
      <AgentFormDialog
        open={createAgentDialogOpen}
        onClose={() => setCreateAgentDialogOpen(false)}
        onSuccess={(newAgent) => {
          // Refresh agents list and select the newly created agent
          queryClient.invalidateQueries({ queryKey: ['agents'] });
          queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
          if (newAgent?.id) {
            setFormData((prev) => ({ ...prev, agent: newAgent.id }));
            setSelectedAgentForDetail(newAgent);
          }
          setCreateAgentDialogOpen(false);
        }}
      />
    </Card>
  );
};

export default AgentSchedulePanel;
